/* eslint-disable no-param-reassign */
import { useLocation, useHistory } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { convertQueryToString, convertStringBackToQuery } from 'helpers/queryString';
import { defaultFilters } from '../../../components/organisms/contactsFilters/defaultFilters';

const paramsToKeep = new Set(['aiFilters', 'aiFilterLoading']);

const getDefaultsFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const result = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const key of urlParams.keys()) {
    if (paramsToKeep.has(key)) {
      // Re-encode: `get` decodes, and appending raw would truncate a prompt containing & or =.
      result.push(`${key}=${encodeURIComponent(urlParams.get(key))}`);
    }
  }

  return result.join('&');
};

export function isDefaultQuery(filters) {
  return isEqual(defaultFilters, filters);
}

export const useContactsSearchParam = (defaultQueryFilters = { filters: defaultFilters }, defaultSort = {}) => {
  const { search } = useLocation();
  const { push } = useHistory();
  const [page, setPage] = useState(1);

  const params = useMemo(() => {
    const { filters, ...others } = search ? convertStringBackToQuery(search) : defaultQueryFilters;

    // Create a stable object reference to prevent unnecessary re-renders
    const mergedFilters = { ...defaultQueryFilters.filters, ...filters };

    return {
      ...defaultQueryFilters,
      ...others,
      filters: mergedFilters,
    };
  }, [search, defaultQueryFilters]);

  const setSearchParams = useCallback(
    (value) => {
      let newValues = { ...value };
      if (typeof value === 'function') {
        newValues = value(params);
      }

      setPage((current) => {
        if (newValues.page && current !== newValues.page) {
          return newValues.page;
        }

        return current;
      });

      delete newValues.page;

      const defaultsFromUrl = getDefaultsFromUrl();
      const defaultSearchValue = defaultsFromUrl ? `?${defaultsFromUrl}` : '';

      // getDefaultsFromUrl is the single source for these; serializing them from newValues too
      // appends a stale second copy, which wins on read.
      const queryValues = { ...newValues };
      paramsToKeep.forEach((key) => delete queryValues[key]);

      push({
        search:
          isEqual(defaultQueryFilters.filters, newValues.filters) &&
          ((typeof newValues.filters?.sortBy === 'undefined' &&
            typeof newValues.filters?.sortDirection === 'undefined') ||
            (newValues.filters?.sortBy === defaultSort.sortBy &&
              newValues.filters?.sortDirection === defaultSort.sortDirection))
            ? defaultSearchValue
            : convertQueryToString(queryValues, defaultsFromUrl),
      });

      return newValues;
    },
    [defaultQueryFilters.filters, params, push, defaultSort],
  );

  // Memoize the returned array to prevent unnecessary re-renders
  const result = useMemo(() => [{ ...params, page }, setSearchParams], [params, page, setSearchParams]);

  return result;
};
